// A run's request to read one resource's contents, and the workspace's answer (#263, C6):
// the card the Inbox's drawer and the investigation page show (#808).
//
// A resource's contents — an object's body, a table's items, a function's
// package — are the customer's data itself, and every read of them is refused
// until the workspace consents, one resource at a time. A run that needs one asks
// and pauses; this card is where the answer is given. Three answers, and each
// is one word on the wire (`api/consent.ts`): allow for this run, always allow
// this resource (which proves presence again, #17 round 2 Q4, through the shared
// step-up dialog and whichever factor the user holds, #729), or decline.
// Unanswered, the run goes on without the contents when its window closes
// (#17 round 3 Q6), and the card says when that is.
//
// The standing grants are listed under the Inbox's Data access filter: what every
// later run of the workspace may read without asking again.

import { useEffect, useState, type FormEvent } from "react";

import {
  ANSWER_LABELS,
  CONSENT_ANSWERS,
  CONSENT_STEP_UP_PURPOSE,
  CONSENT_POLICY_WRITE_PERMISSION,
  MAX_TIMEOUT_SECONDS,
  readConsentPolicy,
  saveConsentPolicy,
  type ConsentAnswer,
  type ConsentPolicy,
  type ConsentOutcome,
  type ConsentRequestRow,
  type StandingGrantRow,
} from "../api/consent";
import { useDeadline } from "./decisionState";
import { browserApi, messageOf, type Api } from "../api/http";
import { listRoles, roleHolds, type Role } from "../api/roles";
import { onOwnMembership, type SessionResponse } from "../api/session";
import type { StepUp } from "../api/policy";
import { Chip, Notice } from "./chrome";
import {
  StepUpDialog,
  useStepUpAttempt,
  type BeginPasskey,
  type FactorsReading,
} from "./StepUp";

export type OnAnswerConsent = (
  requestId: string,
  answer: ConsentAnswer,
  stepUp: StepUp | null,
) => Promise<ConsentOutcome | void>;

/** How each answer's button reads: the narrow allow is the primary, the decline is the danger. */
const ANSWER_CLASSES: Record<ConsentAnswer, string> = {
  run: "btn btn--primary",
  always: "btn",
  declined: "btn btn--danger",
};

export function ConsentCard({
  row,
  onAnswer,
  factors,
  beginPasskey,
}: {
  row: ConsentRequestRow;
  onAnswer: OnAnswerConsent;
  /** What the session holds, from `GET /auth/mfa`; unread, nothing is offered (#739). */
  factors: FactorsReading;
  /** Mint the assertion challenge for the dialog's passkey. */
  beginPasskey?: BeginPasskey;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState(false);
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const expired = useDeadline(row.expires_at);
  const off = busy || expired || answered || row.may_answer !== true;

  const answer = async (choice: ConsentAnswer) => {
    // "Always" is standing authority: no proof, nothing is sent (#659).
    const always = choice === "always";
    if (always && proof === null) return;
    setBusy(true);
    setError(null);
    try {
      await onAnswer(row.request_id, choice, always ? proof : null);
      setAnswered(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
      if (always) spent();
    }
  };

  return (
    <section className="panel decision-card" data-testid="consent-request">
      <div className="panel__head">
        <h2>
          <code>{row.resource_arn}</code>
        </h2>
        <span className="row row--wrap">
          {row.operations.map((operation) => (
            <Chip key={operation}>{operation}</Chip>
          ))}
        </span>
      </div>
      <div className="panel__body stack stack--tight">
        <p>
          {row.reason === "" ? (
            <span className="muted">The investigation gave no reason.</span>
          ) : (
            row.reason
          )}
        </p>
        <p className="muted">
          Asked by investigation <a href={`/runs/${row.run_id}`}>{row.run_id}</a>.
          Unanswered, it goes on without these contents at{" "}
          {row.expires_at || "a deadline not given"}.
        </p>
        {row.may_answer === true ? null : (
          <Notice tone="bad">
            You cannot answer this consent request. A workspace member with
            investigation management permission must answer; delegated access
            does not carry this authority.
          </Notice>
        )}
        {expired ? (
          <Notice tone="bad">
            This request has expired. Refresh to read the current consent state.
          </Notice>
        ) : null}
        {busy ? <p role="status">Recording your answer…</p> : null}
        {error === null ? null : (
          <Notice tone="bad">
            {error} Refresh to read the current request before trying again.
          </Notice>
        )}
        <StepUpDialog
          key={attempt}
          purpose={CONSENT_STEP_UP_PURPOSE}
          changeId={row.request_id}
          factors={factors}
          disabled={off}
          beginPasskey={beginPasskey}
          onProofChange={setProof}
          onError={setError}
        />
        <div className="actions">
          {CONSENT_ANSWERS.map((choice) => (
            <button
              key={choice}
              type="button"
              className={ANSWER_CLASSES[choice]}
              disabled={off || (choice === "always" && unproven)}
              onClick={() => void answer(choice)}
            >
              {ANSWER_LABELS[choice]}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The standing grants: what every later run of the workspace may read without asking.
 * Shown under the Inbox's Data access filter (#808), for the selected workspace.
 */
export function StandingGrants({ grants, workspaceName }: { grants: StandingGrantRow[]; workspaceName: string }) {
  return (
    <section className="panel">
      <div className="panel__head">
        <h2>Standing data access in {workspaceName}</h2>
        <Chip>{grants.length} held</Chip>
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">
          Resources whose contents every later investigation of this workspace
          may read without asking.
        </p>
        {grants.length === 0 ? (
          <p className="muted">None. Every read of contents asks first.</p>
        ) : null}
      </div>
      {grants.length === 0 ? null : (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Operations</th>
                <th>Allowed</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => (
                <tr key={grant.grant_id}>
                  <td>
                    <code>{grant.resource_arn}</code>
                  </td>
                  <td>{grant.operations.join(", ")}</td>
                  <td>{grant.granted_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Whether this workspace's runs may ask it for consent: off until someone with the authority to
 * answer turns it on, here (#1453). Drawn for everyone and disabled for a role that may not
 * write it, with the reason in words; the server is the enforcement.
 */
export function ConsentPolicyControl({ session, api = browserApi }: { session: SessionResponse; api?: Api }) {
  const [policy, setPolicy] = useState<ConsentPolicy | null>(null);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [timeout, setTimeoutText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    readConsentPolicy(api).then(
      (read) => {
        setPolicy(read);
        setTimeoutText(read.timeout_seconds === null ? "" : String(read.timeout_seconds));
      },
      (failure) => setError(messageOf(failure)),
    );
    listRoles(api).then(setRoles, () => setRoles(null));
  }, [api]);

  const mayWrite = onOwnMembership(session) && roleHolds(roles, session.role, CONSENT_POLICY_WRITE_PERMISSION);
  const borrowed = Boolean(session.acting_org_id);

  const save = async (next: ConsentPolicy) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const written = await saveConsentPolicy(api, next);
      setPolicy(written);
      setTimeoutText(written.timeout_seconds === null ? "" : String(written.timeout_seconds));
      setSaved(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = timeout.trim();
    const seconds = Number(trimmed);
    if (trimmed !== "" && (!Number.isInteger(seconds) || seconds < 1 || seconds > MAX_TIMEOUT_SECONDS)) {
      // Never send a number the field did not hold: `Number("abc")` is NaN, which JSON writes as null.
      setSaved(false);
      setError(`The timeout is a whole number of seconds, from 1 to ${MAX_TIMEOUT_SECONDS.toLocaleString("en-US")} (one day). Leave it empty for the default.`);
      return;
    }
    void save({ enabled: true, timeout_seconds: trimmed === "" ? null : seconds });
  };

  return (
    <section className="panel" aria-labelledby="consent-policy" data-testid="consent-policy">
      <div className="panel__head">
        <h2 id="consent-policy">Consent requests</h2>
        {policy === null ? null : <Chip>{policy.enabled ? "On" : "Off"}</Chip>}
      </div>
      <form className="panel__body stack stack--tight" onSubmit={submit} aria-busy={busy}>
        <p className="muted">
          Off, an investigation never asks to read the contents of a resource. On, it may ask, and
          each request waits here for one person&rsquo;s answer.
        </p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <p className="field">
          <label htmlFor="consent-policy-enabled" className="row">
            <input
              id="consent-policy-enabled"
              type="checkbox"
              checked={policy?.enabled === true}
              disabled={policy === null || !mayWrite || busy}
              onChange={(event) => {
                if (!mayWrite) return;
                void save({
                  enabled: event.target.checked,
                  // A disable carries no timeout, and a stored one may be past the maximum the server now accepts.
                  timeout_seconds: event.target.checked ? (policy?.timeout_seconds ?? null) : null,
                });
              }}
            />
            Let investigations ask this workspace for consent
          </label>
        </p>
        {policy === null || mayWrite ? null : (
          <p className="muted" id="consent-policy-reason">
            {borrowed
              ? "This is the workspace's own decision; a partner's access does not carry it."
              : "Only a member who can manage investigations can change this."}
          </p>
        )}
        {policy?.enabled !== true ? null : (
          <p className="field">
            <label htmlFor="consent-policy-timeout" className="field__label">
              Answer within (seconds)
            </label>
            <input
              id="consent-policy-timeout"
              className="input input--inline"
              type="number"
              min={1}
              max={MAX_TIMEOUT_SECONDS}
              disabled={!mayWrite || busy}
              value={timeout}
              onChange={(event) => {
                setSaved(false);
                setTimeoutText(event.target.value);
              }}
            />
            <small className="field__hint">Left empty, the platform&rsquo;s default of ten minutes.</small>
          </p>
        )}
        {policy?.enabled === true && mayWrite ? (
          <div className="actions">
            <button type="submit" className="btn btn--small" disabled={busy}>
              Save the timeout
            </button>
            {saved ? <span role="status">Saved</span> : null}
          </div>
        ) : null}
      </form>
    </section>
  );
}
